import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, Bot, User as UserIcon, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { DEFAULT_SITE_CONTENT } from "@/lib/defaultSiteContent";

const SESSION_KEY = "autopark_support_session";
const HISTORY_KEY = "autopark_support_history";

function makeWelcome(text) {
  return { role: "bot", text: text || DEFAULT_SITE_CONTENT.support_chatbot.welcome_message };
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = "s" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch (_) { return "s-fallback-" + Date.now(); }
}

export function SupportChatbot() {
  const { user } = useAuth();
  const { content } = useSiteContent();
  const chatCfg = content.support_chatbot || DEFAULT_SITE_CONTENT.support_chatbot;
  const welcome = useMemo(() => makeWelcome(chatCfg.welcome_message), [chatCfg.welcome_message]);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = sessionStorage.getItem(HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : [welcome];
    } catch (_) { return [welcome]; }
  });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadedFromServer, setLoadedFromServer] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const scrollerRef = useRef(null);

  // Bejelentkezett usernek első nyitáskor betöltjük a szerver oldali chat előzményét
  useEffect(() => {
    if (!open || !user || loadedFromServer) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/support/history?limit=50");
        if (cancelled) return;
        const rows = Array.isArray(r.data?.messages) ? r.data.messages : [];
        if (rows.length > 0) {
          const flat = [];
          for (const row of rows) {
            if (row.user_text) flat.push({ role: "user", text: row.user_text });
            if (row.bot_reply) flat.push({ role: "bot", text: row.bot_reply });
          }
          setMessages([welcome, ...flat]);
        }
      } catch (_) { /* csendben, marad a sessionStorage state */ }
      setLoadedFromServer(true);
    })();
    return () => { cancelled = true; };
  }, [open, user, loadedFromServer]);

  // Ki- / bejelentkezéskor reset — hogy másik user ne lássa az előzőt
  useEffect(() => {
    setLoadedFromServer(false);
    setMessages([welcome]);
    try { sessionStorage.removeItem(HISTORY_KEY); } catch (_) {}
  }, [user?.id, welcome.text]);

  useEffect(() => {
    try { sessionStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-30))); } catch (_) {}
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  // Prediktív GYIK: debounce-olt lekérdezés a szerver top_questions történetéből
  useEffect(() => {
    if (!open) return;
    const q = input.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/support/suggestions?q=${encodeURIComponent(q)}&limit=3`);
        const items = Array.isArray(r.data?.suggestions) ? r.data.suggestions : [];
        // ne javasoljuk vissza pontosan azt amit épp gépel
        const ql = q.toLowerCase();
        setSuggestions(items.filter((s) => (s.question || "").toLowerCase() !== ql).slice(0, 3));
      } catch (_) { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [input, open]);

  const clearHistory = async () => {
    if (!window.confirm("Biztosan törlöd a teljes chat előzményt?")) return;
    setMessages([welcome]);
    try { sessionStorage.removeItem(HISTORY_KEY); } catch (_) {}
    if (user) {
      try { await api.delete("/support/history"); } catch (_) {}
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const r = await api.post("/support/chat", { session_id: getSessionId(), message: msg });
      setMessages((m) => [...m, { role: "bot", text: r.data?.reply || "Sajnos most nem tudok válaszolni. Próbáld újra pár másodperc múlva." }]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errMsg = typeof detail === "string" ? detail : "Az asszisztens jelenleg nem elérhető, kérlek próbáld később vagy írj az info@autopark.hu címre.";
      setMessages((m) => [...m, { role: "bot", text: errMsg, error: true }]);
    } finally { setSending(false); }
  };

  const welcomeSuggestions = [
    "Hogyan működik a kredit rendszer?",
    "Hogyan adhatok fel hirdetést?",
    "Mennyibe kerül a 360° körbeforgatás?",
    "Hogyan léphetek kapcsolatba az adminnal?",
  ];

  return (
    <>
      {/* Floating button */}
      <button
        data-testid="support-chat-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Chat bezárása" : "Ügyfélszolgálati chat"}
        className={`fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-all duration-200 ${open ? "bg-slate-700 hover:bg-slate-600 rotate-90" : "bg-electric hover:bg-electric-light hover:scale-110"}`}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" strokeWidth={1.75} />}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="support-chat-panel"
          className="fixed bottom-24 right-5 z-50 w-[calc(100vw-40px)] sm:w-96 max-h-[70vh] rounded-3xl overflow-hidden shadow-2xl border border-white/10 bg-anthracite-900 flex flex-col"
          style={{ maxHeight: "min(70vh, 640px)" }}
        >
          <div className="p-4 bg-anthracite-800 border-b border-white/5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-electric/15 flex items-center justify-center">
              <Bot className="w-4 h-4 text-electric" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <div className="font-heading text-sm text-white">{chatCfg.header_title}</div>
              <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online · Ingyenes {user && "· Mentett előzmény"}
              </div>
            </div>
            {messages.length > 1 && (
              <button
                data-testid="support-chat-clear"
                onClick={clearHistory}
                title="Előzmény törlése"
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/[0.06] text-slate-400 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div ref={scrollerRef} data-testid="support-chat-scroller" className="flex-1 overflow-y-auto p-4 space-y-3 bg-anthracite-900">
            {messages.map((m, i) => (
              <div key={i} data-testid={`support-msg-${m.role}-${i}`} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "bot" && (
                  <div className="w-7 h-7 rounded-full bg-electric/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-electric" />
                  </div>
                )}
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-electric text-white rounded-br-md"
                    : m.error
                    ? "bg-red-500/10 border border-red-400/20 text-red-100 rounded-bl-md"
                    : "bg-white/[0.04] border border-white/5 text-slate-100 rounded-bl-md"
                }`}>
                  {m.text.split("\n").map((line, j) => (
                    <div key={j}>{line || "\u00A0"}</div>
                  ))}
                </div>
                {m.role === "user" && (
                  <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                    <UserIcon className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div className="flex gap-2 justify-start" data-testid="support-typing">
                <div className="w-7 h-7 rounded-full bg-electric/15 flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-electric" />
                </div>
                <div className="bg-white/[0.04] border border-white/5 rounded-2xl px-3.5 py-2 text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-electric" />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="support-suggestions">
              {welcomeSuggestions.map((s) => (
                <button
                  key={s}
                  data-testid={`support-suggestion-${s.slice(0, 10)}`}
                  onClick={() => { setInput(s); }}
                  className="px-2.5 py-1 rounded-full bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 text-[11px] text-slate-300 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={send} className="p-3 border-t border-white/5 bg-anthracite-800">
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="support-predictive-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`support-predictive-${i}`}
                    onClick={() => { setInput(s.question); setSuggestions([]); }}
                    className="text-left px-2.5 py-1 rounded-lg bg-electric/10 hover:bg-electric/20 border border-electric/20 text-[11px] text-slate-200 transition-colors max-w-full truncate"
                    title={s.question}
                  >
                    {s.question}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                data-testid="support-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={chatCfg.input_placeholder}
                maxLength={1000}
                disabled={sending}
                className="flex-1 bg-anthracite-900 border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:border-electric outline-none disabled:opacity-60"
              />
              <button
                type="submit"
                data-testid="support-chat-send"
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-full bg-electric hover:bg-electric-light disabled:opacity-40 flex items-center justify-center text-white transition-colors"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
